import { useMemo, useState } from "react";
import { AddressElement, Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe, type Appearance } from "@stripe/stripe-js";
import { Button, Field, Input, SelectX } from "@guestpilot/ui-kit";
import { ENV } from "../env";
import { useI18n } from "../i18n";

/** Stripe Elements appearance built from the design tokens (04 §8): the card form looks like the other inputs, in both themes. */
export function appearanceFromTokens(root: Element = document.documentElement): Appearance {
  const css = getComputedStyle(root);
  const v = (name: string) => css.getPropertyValue(name).trim();
  return {
    theme: root.closest('[data-theme="dark"]') ? "night" : "stripe",
    variables: {
      colorPrimary: v("--brand-solid"),
      colorBackground: v("--surface-sunken"),
      colorText: v("--text-primary"),
      colorDanger: v("--danger-text"),
      fontFamily: v("--font-sans"),
      borderRadius: v("--radius-md"),
    },
  };
}

interface Props {
  clientSecret: string;
  publishableKey: string;
  submitLabel: string;
  busy?: boolean;
  /** receives the confirmed payment method; the form stays busy until the returned promise settles (subscription saved, page refreshed) */
  onPaymentMethod: (paymentMethodId: string) => void | Promise<unknown>;
  /** runs before the card is confirmed (the billing email and the VAT number are checked and sent); `false` stops the payment */
  beforeSubmit?: () => Promise<boolean> | boolean;
  /** the billing country and postal code as they are typed in the Address Element: they only serve the VAT estimate (D-161) */
  onAddress?: (a: { country: string; postalCode: string }) => void;
}

/**
 * Card details never reach GuestPilot's servers: the SetupIntent is confirmed in Stripe's own element, with 3-D Secure in Stripe's
 * frame (BR-262). The business name and the billing address are entered in Stripe's Address Element and travel with the card as its
 * billing details: GuestPilot does not receive them (D-160).
 */
export function PaymentForm(props: Props) {
  if (ENV.mock) return <MockCard {...props} />;
  return <StripeCard {...props} />;
}

function StripeCard({
  clientSecret,
  publishableKey,
  submitLabel,
  busy,
  onPaymentMethod,
  beforeSubmit,
  onAddress,
}: Props) {
  const { locale } = useI18n();
  const stripePromise = useMemo(() => loadStripe(publishableKey), [publishableKey]);
  const options = useMemo(
    () => ({ clientSecret, appearance: appearanceFromTokens(), locale: locale as "en" | "fr" | "de" | "es" }),
    [clientSecret, locale],
  );
  return (
    <Elements stripe={stripePromise} options={options}>
      <Inner
        submitLabel={submitLabel}
        busy={busy}
        onPaymentMethod={onPaymentMethod}
        beforeSubmit={beforeSubmit}
        onAddress={onAddress}
      />
    </Elements>
  );
}

function Inner({
  submitLabel,
  busy,
  onPaymentMethod,
  beforeSubmit,
  onAddress,
}: Pick<Props, "submitLabel" | "busy" | "onPaymentMethod" | "beforeSubmit" | "onAddress">) {
  const { t } = useI18n();
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  // one busy state from the click to the end of the whole payment (card confirmation, then the subscription saved and the page refreshed):
  // the button never comes back before the dialog closes, so the payment cannot be started twice
  const submit = async () => {
    if (!stripe || !elements || working) return;
    setError(null);
    setWorking(true);
    try {
      if (beforeSubmit && !(await beforeSubmit())) return;
      // the company name and the address are Stripe's: asked for in its Address Element, sent to Stripe with the card
      const address = await elements.getElement(AddressElement)?.getValue();
      if (!address?.complete) {
        setError(t("ui:subscription.addressIncomplete"));
        return;
      }
      const res = await stripe.confirmSetup({
        elements,
        redirect: "if_required",
        confirmParams: {
          payment_method_data: {
            billing_details: { name: address.value.name, address: address.value.address },
          },
        },
      });
      if (res.error) setError(res.error.message ?? t("errors:internal_error"));
      else if (res.setupIntent?.payment_method) await onPaymentMethod(String(res.setupIntent.payment_method));
    } finally {
      setWorking(false);
    }
  };
  return (
    <div className="gp-stack">
      <h3 className="t-body-strong" style={{ margin: 0 }}>
        {t("ui:subscription.companyTitle")}
      </h3>
      <p className="t-body-sm gp-muted" style={{ margin: 0 }}>
        {t("ui:subscription.companyLead")}
      </p>
      <AddressElement
        options={{ mode: "billing", display: { name: "organization" } }}
        onChange={(e) =>
          onAddress?.({
            country: e.value.address.country ?? "",
            postalCode: e.value.address.postal_code ?? "",
          })
        }
      />
      <h3 className="t-body-strong" style={{ margin: 0 }}>
        {t("ui:subscription.paymentTitle")}
      </h3>
      <PaymentElement options={{ fields: { billingDetails: { name: "never", address: "never" } } }} />
      {error ? (
        <p role="alert" className="t-body-sm" style={{ margin: 0, color: "var(--danger-text)" }}>
          {error}
        </p>
      ) : null}
      <div className="gp-pay-bar">
        <Button variant="primary" size="lg" disabled={!stripe || working || busy} onClick={submit}>
          {working || busy ? t("ui:subscription.processing") : submitLabel}
        </Button>
        {working || busy ? (
          <p
            role="status"
            className="t-body-sm gp-muted"
            style={{ margin: "var(--space-8) 0 0", textAlign: "center" }}
          >
            {t("ui:subscription.processingHint")}
          </p>
        ) : null}
      </div>
    </div>
  );
}

/** Development and test stand-in for the Stripe elements (no network, no card data). */
function MockCard({ submitLabel, busy, onPaymentMethod, beforeSubmit, onAddress }: Props) {
  const { t } = useI18n();
  const [card, setCard] = useState("");
  const [country, setCountry] = useState("");
  const [working, setWorking] = useState(false);
  return (
    <div className="gp-stack">
      <Field label="Country">
        <SelectX
          value={country}
          options={[
            { value: "", label: "—" },
            { value: "FR", label: "France" },
            { value: "DE", label: "Deutschland" },
            { value: "US", label: "United States" },
          ]}
          onChange={(e) => {
            setCountry(e.target.value);
            onAddress?.({ country: e.target.value, postalCode: "" });
          }}
        />
      </Field>
      <Field label={t("ui:subscription.card")} help={t("ui:subscription.mockCard")}>
        <Input
          value={card}
          onChange={(e) => setCard(e.target.value)}
          inputMode="numeric"
          autoComplete="off"
        />
      </Field>
      <div className="gp-pay-bar">
        <Button
          variant="primary"
          size="lg"
          disabled={busy || working}
          onClick={async () => {
            setWorking(true);
            try {
              if (beforeSubmit && !(await beforeSubmit())) return;
              await onPaymentMethod("pm_mock_4242");
            } finally {
              setWorking(false);
            }
          }}
        >
          {working || busy ? t("ui:subscription.processing") : submitLabel}
        </Button>
      </div>
    </div>
  );
}
